/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApplicantSearchSettingsRequest } from '../models/ApplicantSearchSettingsRequest';
import type { ApplicantSearchSettingsResponse } from '../models/ApplicantSearchSettingsResponse';
import type { CompletedStage } from '../models/CompletedStage';
import type { DuplicateApplicantSettingsRejectionReasonsResponse } from '../models/DuplicateApplicantSettingsRejectionReasonsResponse';
import type { DuplicateApplicantSettingsRequest } from '../models/DuplicateApplicantSettingsRequest';
import type { DuplicateApplicantSettingsResponse } from '../models/DuplicateApplicantSettingsResponse';
import type { DuplicateApplicantSettingsUpdateResponse } from '../models/DuplicateApplicantSettingsUpdateResponse';
import type { EmailMessageContent } from '../models/EmailMessageContent';
import type { Emails } from '../models/Emails';
import type { HiringGoal } from '../models/HiringGoal';
import type { HiringGoalBulkCreateUpdateResponse } from '../models/HiringGoalBulkCreateUpdateResponse';
import type { InitiateTelephoneCallResponse } from '../models/InitiateTelephoneCallResponse';
import type { LocationGroup } from '../models/LocationGroup';
import type { Opening } from '../models/Opening';
import type { Pagination } from '../models/Pagination';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ApiSelfServeV2Service {

    /**
     * show applicant search settings
     * Returns the applicant search settings for the account
     * @returns ApplicantSearchSettingsResponse successful
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantSearchSettings(): CancelablePromise<ApplicantSearchSettingsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicant_search_settings',
        });
    }

    /**
     * update applicant search settings
     * Updates the applicant search settings for the account
     * @param requestBody
     * @returns any successful update
     * @throws ApiError
     */
    public static patchApiSelfServeV2ApplicantSearchSettings(
        requestBody?: ApplicantSearchSettingsRequest,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api_self_serve/v2/applicant_search_settings',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * index
     * Returns list of email conversations
     * @param externalId Applicant's external_id
     * @returns Emails success
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantsEmailConversations(
        externalId: string,
    ): CancelablePromise<Emails> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants/{external_id}/email_conversations',
            path: {
                'external_id': externalId,
            },
        });
    }

    /**
     * email_content
     * Returns email content
     * @param externalId Applicant's external_id
     * @param emailMessageExternalId EmailMessage external_id
     * @returns EmailMessageContent success
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantsEmailMessagesEmailContent(
        externalId: string,
        emailMessageExternalId: string,
    ): CancelablePromise<EmailMessageContent> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants/{external_id}/email_messages/{email_message_external_id}/email_content',
            path: {
                'external_id': externalId,
                'email_message_external_id': emailMessageExternalId,
            },
        });
    }

    /**
     * initiate_call
     * Starts a call with the applicant
     * @param externalId Applicant's external_id
     * @returns InitiateTelephoneCallResponse success
     * @throws ApiError
     */
    public static postApiSelfServeV2ApplicantsTelephonyInitiateCall(
        externalId: string,
    ): CancelablePromise<InitiateTelephoneCallResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/applicants/{external_id}/telephony/initiate_call',
            path: {
                'external_id': externalId,
            },
            errors: {
                422: `unprocessable`,
            },
        });
    }

    /**
     * Get completed stages for an applicant
     * Returns all completed stages for a specific applicant, including sub-stages for multi-stages
     * @param externalId External ID for the applicant
     * @param includeCurrentStage Whether to include the current stage
     * @param scopeToCurrentOpening When true, only return stages in the applicant's current opening workflow (for print stage picker)
     * @returns any successful
     * @throws ApiError
     */
    public static getApiSelfServeV2ApplicantsTransitionsCompletedStages(
        externalId: string,
        includeCurrentStage?: boolean,
        scopeToCurrentOpening?: boolean,
    ): CancelablePromise<{
        /**
         * Array of completed stages sorted by completion date (earliest first)
         */
        completed_stages: Array<CompletedStage>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/applicants/{external_id}/transitions/completed_stages',
            path: {
                'external_id': externalId,
            },
            query: {
                'include_current_stage': includeCurrentStage,
                'scope_to_current_opening': scopeToCurrentOpening,
            },
        });
    }

    /**
     * resubmit_partner_data
     * Re-sends partner data for a specified applicant
     * @param externalId External ID for the applicant
     * @param requestBody Partner information
     * @returns any success
     * @throws ApiError
     */
    public static postApiSelfServeV2ApplicantsResubmitPartnerData(
        externalId: string,
        requestBody: {
            partner_id?: string;
            partner_stage_id?: string;
        },
    ): CancelablePromise<{
        message?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/applicants/{external_id}/resubmit_partner_data',
            path: {
                'external_id': externalId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
                403: `Unauthorized`,
            },
        });
    }

    /**
     * index
     * Returns Hiring Goals
     * @param openingId filter query for opening_id
     * @param page pagination setting for page to view
     * @param perPage pagination setting for max number of records to return
     * @param open filter query for open scope
     * @param active filter query for active scope
     * @param pending filter query for pending scope
     * @param scheduled filter query for scheduled scope
     * @param closed filter query for closed scope
     * @param current filter query for current scope
     * @param past filter query for past scope
     * @param sortBy filter query for sort_by scope
     * @param sortDirection filter query for sort_direction scope
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2BulkHiringGoals(
        openingId: string,
        page: string,
        perPage: string,
        open?: string,
        active?: string,
        pending?: string,
        scheduled?: string,
        closed?: string,
        current?: string,
        past?: string,
        sortBy?: string,
        sortDirection?: string,
    ): CancelablePromise<{
        hiring_goals: Array<HiringGoal>;
        metadata?: {
            goal: number;
            hires_count: number;
            count_by_status: {
                active: number;
                pending: number;
                scheduled: number;
                closed: number;
            };
        };
        /**
         * Pagination info (empty object when no results)
         */
        pagination: {
            current_page?: number | null;
            next_page?: number | null;
            per_page?: number | null;
            total_pages?: number | null;
            total_count?: number | null;
        };
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/bulk_hiring_goals',
            query: {
                'opening_id': openingId,
                'open': open,
                'active': active,
                'pending': pending,
                'scheduled': scheduled,
                'closed': closed,
                'current': current,
                'past': past,
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
            },
        });
    }

    /**
     * create_or_update
     * Bulk Creates or Updates Hiring Goals
     * @param requestBody Hiring Goals
     * @returns HiringGoalBulkCreateUpdateResponse success
     * @throws ApiError
     */
    public static postApiSelfServeV2BulkHiringGoalsCreateOrUpdate(
        requestBody: {
            goals: Array<{
                goal: number;
                hire_by?: string;
                start_date?: string;
                automatically_close_on_date?: string;
                funnel_id?: string;
                external_id?: string;
                form_index?: number;
            }>;
            funnel_id: string;
        },
    ): CancelablePromise<HiringGoalBulkCreateUpdateResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api_self_serve/v2/bulk_hiring_goals/create_or_update',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized`,
            },
        });
    }

    /**
     * destroy
     * Closes or Soft Deletes a Hiring Goal
     * @param externalId Hiring Goal External ID
     * @returns void
     * @throws ApiError
     */
    public static deleteApiSelfServeV2BulkHiringGoals(
        externalId: string,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api_self_serve/v2/bulk_hiring_goals/{external_id}',
            path: {
                'external_id': externalId,
            },
            errors: {
                401: `unauthorized`,
                422: `unprocessable`,
            },
        });
    }

    /**
     * index
     * Returns a list of data keys or full data key objects if get_data_key_objects is true
     * @param getDataKeyWithOptions If true, returns full data key objects instead of just keys
     * @param getDataKeyObjects If true, returns full data key objects instead of just keys
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2DataKeys(
        getDataKeyWithOptions?: boolean,
        getDataKeyObjects?: boolean,
    ): CancelablePromise<{
        data_keys: Array<(string | {
            id?: number;
            key?: string;
            created_at?: string;
            concept_id?: string | null;
            key_type?: string;
            restricted?: boolean;
            storage?: string;
            title?: string;
        } | {
            id?: number;
            key?: string;
            created_at?: string;
            concept_id?: string | null;
            key_type?: string;
            restricted?: boolean;
            storage?: string;
            title?: string;
            possible_values?: Array<string>;
        })>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/data_keys',
            query: {
                'get_data_key_with_options': getDataKeyWithOptions,
                'get_data_key_objects': getDataKeyObjects,
            },
        });
    }

    /**
     * update
     * Updates a specific data key
     * @param id Data key's ID
     * @param requestBody
     * @returns any success
     * @throws ApiError
     */
    public static putApiSelfServeV2DataKeys(
        id: number,
        requestBody?: {
            concept_id?: string | null;
            concept_option_mappings?: Record<string, string>;
        },
    ): CancelablePromise<{
        message: string;
        data_key: {
            id?: number;
            key?: string;
            concept_id?: string | null;
        };
    }> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api_self_serve/v2/data_keys/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * show duplicate applicant settings
     * Returns the duplicate applicant detection settings for the account
     * @returns DuplicateApplicantSettingsResponse successful
     * @throws ApiError
     */
    public static getApiSelfServeV2DuplicateApplicantSettings(): CancelablePromise<DuplicateApplicantSettingsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/duplicate_applicant_settings',
        });
    }

    /**
     * update duplicate applicant settings
     * Updates the duplicate applicant detection settings for the account
     * @param requestBody
     * @returns DuplicateApplicantSettingsUpdateResponse successful update
     * @throws ApiError
     */
    public static patchApiSelfServeV2DuplicateApplicantSettings(
        requestBody?: DuplicateApplicantSettingsRequest,
    ): CancelablePromise<DuplicateApplicantSettingsUpdateResponse> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api_self_serve/v2/duplicate_applicant_settings',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `validation errors`,
            },
        });
    }

    /**
     * list rejection reasons available for duplicate handling
     * Returns the rejection reasons the account can apply to duplicate applicants
     * @returns DuplicateApplicantSettingsRejectionReasonsResponse successful
     * @throws ApiError
     */
    public static getApiSelfServeV2DuplicateApplicantSettingsRejectionReasons(): CancelablePromise<DuplicateApplicantSettingsRejectionReasonsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/duplicate_applicant_settings/rejection_reasons',
        });
    }

    /**
     * index
     * Returns location groups
     * @param query Filter location groups returned by matching title
     * @param userId Filters by user accessible funnels (requires enterprise=true)
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2LocationGroups(
        query?: string,
        userId?: string,
    ): CancelablePromise<{
        location_groups: Array<LocationGroup>;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/location_groups',
            query: {
                'query': query,
                'user_id': userId,
            },
        });
    }

    /**
     * show
     * Returns a location group
     * @param id Location group's external_id
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2LocationGroups1(
        id: string,
    ): CancelablePromise<{
        location_group: LocationGroup;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/location_groups/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * index
     * Returns Openings and Pagination
     * @param workflowId Determines which workflows to return openings from
     * @param title Filter openings returned by matching title
     * @param page Determines which page to return
     * @param perPage number of results to return per page
     * @param sortBy field to sort results by
     * @param sortDirection direction to sort results
     * @param active Determines whether to return active or inactive openings
     * @param query Filter openings returned by matching opening/location/position/brand names
     * @param locationIds filters openings with given location ids
     * @param positionIds filters openings with given position ids
     * @param brandIds filters openings with given brand ids
     * @param isSelectedForSourcing filters openings which is selected for sourcing dashboard
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2Openings(
        workflowId?: string,
        title?: string,
        page?: number,
        perPage?: number,
        sortBy?: string,
        sortDirection?: 'asc' | 'desc',
        active?: boolean,
        query?: string,
        locationIds?: Array<string>,
        positionIds?: Array<string>,
        brandIds?: Array<string>,
        isSelectedForSourcing?: boolean,
    ): CancelablePromise<{
        openings: Array<Opening>;
        pagination: Pagination;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings',
            query: {
                'workflow_id': workflowId,
                'title': title,
                'page': page,
                'per_page': perPage,
                'sort_by': sortBy,
                'sort_direction': sortDirection,
                'active': active,
                'query': query,
                'location_ids[]': locationIds,
                'position_ids[]': positionIds,
                'brand_ids[]': brandIds,
                'is_selected_for_sourcing': isSelectedForSourcing,
            },
        });
    }

    /**
     * show
     * Returns a single opening JSON representation
     * @param externalId Opening#external_id
     * @returns any success
     * @throws ApiError
     */
    public static getApiSelfServeV2Openings1(
        externalId: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/openings/{external_id}',
            path: {
                'external_id': externalId,
            },
        });
    }

    /**
     * update
     * Updates an opening
     * @param id Opening#external_id
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public static putApiSelfServeV2Openings(
        id: string,
        requestBody?: {
            /**
             * Opening title
             */
            title?: string;
            /**
             * Whether the opening is active
             */
            active?: boolean;
            /**
             * Additional information about the opening
             */
            additional_info?: string;
            /**
             * Application deadline
             */
            application_deadline?: string;
            /**
             * Compensation amount
             */
            compensation?: string;
            /**
             * Currency code for compensation
             */
            compensation_currency_code?: string;
            /**
             * Type of compensation
             */
            compensation_type?: string;
            /**
             * Minimum compensation
             */
            compensation_min?: number;
            /**
             * Maximum compensation
             */
            compensation_max?: number;
            /**
             * Custom identifier
             */
            custom_id?: string;
            /**
             * Description of the opening
             */
            description?: string;
            /**
             * Force showing compensation
             */
            force_show_compensation?: boolean;
            /**
             * External title
             */
            external_title?: string;
            /**
             * Location external ID
             */
            location_id?: string;
            /**
             * Position external ID
             */
            position_id?: string;
            /**
             * Owner external ID
             */
            owner_id?: string;
            /**
             * Workflow external ID
             */
            workflow_id?: string;
            /**
             * Shift external ID
             */
            shift_id?: string;
            /**
             * Type of job
             */
            job_type?: string;
            /**
             * Job hours
             */
            job_hours?: string;
            /**
             * Job industry
             */
            job_industry?: string;
            /**
             * Whether the opening is private
             */
            is_private?: boolean;
            /**
             * Whether this is a hiring funnel
             */
            is_hiring_funnel?: boolean;
            /**
             * Whether this is a sourcing funnel
             */
            is_sourcing_funnel?: boolean;
            /**
             * Whether this is an internal funnel
             */
            is_internal_funnel?: boolean;
            /**
             * Internal job duration in days
             */
            internal_job_duration?: number;
            /**
             * Internal job expiration date
             */
            internal_job_expiration_date?: string;
            /**
             * Action to take on internal job expiration
             */
            internal_job_expiration_action?: string;
        },
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/api_self_serve/v2/openings/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                403: `unauthorized`,
                422: `unprocessable entity`,
            },
        });
    }

    /**
     * feature flag values for user
     * Returns an object with feature flag values for the specified user.
     * @param id User's external_id
     * @returns any successful
     * @throws ApiError
     */
    public static getApiSelfServeV2UsersFeatures(
        id: string,
    ): CancelablePromise<{
        features: any;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api_self_serve/v2/users/{id}/features',
            path: {
                'id': id,
            },
        });
    }

}